import React from 'react';
import { motion } from 'framer-motion';

interface TileExplosionProps {
  position: { top: string; left: string };
  color?: string;
}

const TileExplosion: React.FC<TileExplosionProps> = ({ position, color = '#4CAF50' }) => {
  // Create more particles for a more dramatic explosion (12 main particles)
  const mainParticles = Array.from({ length: 12 }, (_, i) => {
    const baseAngle = (i * 360) / 12;
    const randomOffset = (Math.random() - 0.5) * 15; // Random spread
    return {
      id: i,
      angle: baseAngle + randomOffset,
      distance: 40 + Math.random() * 25, // Larger, more visible particles
      size: 8 + Math.random() * 6, // Bigger particles
      rotation: Math.random() * 360,
    };
  });

  // Secondary smaller particles for debris effect
  const debrisParticles = Array.from({ length: 8 }, (_, i) => {
    const angle = (i * 360) / 8 + 22.5; // Offset from main particles
    return {
      id: i,
      angle: angle,
      distance: 30 + Math.random() * 20,
      size: 4 + Math.random() * 3,
    };
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        transform: 'translate(-50%, -50%)',
        width: '60px',
        height: '60px',
        pointerEvents: 'none',
        zIndex: 2000, // Higher z-index to ensure visibility
      }}
    >
      {/* Main explosion particles - larger, more visible */}
      {mainParticles.map((particle) => {
        const radians = (particle.angle * Math.PI) / 180;
        const x = Math.cos(radians) * particle.distance;
        const y = Math.sin(radians) * particle.distance;

        return (
          <motion.div
            key={`main-${particle.id}`}
            style={{
              position: 'absolute',
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: color,
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              boxShadow: `0 0 ${particle.size * 1.5}px ${color}, 0 0 ${particle.size * 2.5}px rgba(76, 175, 80, 0.5)`,
              filter: 'blur(1px)',
            }}
            initial={{
              x: 0,
              y: 0,
              scale: 1.5,
              opacity: 1,
              rotate: 0,
            }}
            animate={{
              x: x,
              y: y,
              scale: [1.5, 1.2, 0.3, 0],
              opacity: [1, 1, 0.8, 0],
              rotate: particle.rotation + 360,
            }}
            transition={{
              duration: 0.7,
              ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for explosive feel
              times: [0, 0.2, 0.6, 1],
            }}
          />
        );
      })}
      
      {/* Debris particles */}
      {debrisParticles.map((particle) => {
        const radians = (particle.angle * Math.PI) / 180;
        const x = Math.cos(radians) * particle.distance;
        const y = Math.sin(radians) * particle.distance;

        return (
          <motion.div
            key={`debris-${particle.id}`}
            style={{
              position: 'absolute',
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: color,
              borderRadius: particle.size < 5 ? '50%' : '30%', // Some debris can be square-ish
              top: '50%',
              left: '50%',
              boxShadow: `0 0 ${particle.size}px ${color}`,
            }}
            initial={{
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1,
              rotate: 0,
            }}
            animate={{
              x: x,
              y: y,
              scale: [1, 0.8, 0],
              opacity: [1, 1, 0],
              rotate: Math.random() * 720,
            }}
            transition={{
              duration: 0.8,
              ease: 'easeOut',
              times: [0, 0.4, 1],
              delay: 0.05 + Math.random() * 0.1,
            }}
          />
        );
      })}
      
      {/* Flash effect at center */}
      <motion.div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: color,
          transform: 'translate(-50%, -50%)',
          filter: 'blur(8px)',
          opacity: 0.8,
        }}
        initial={{ scale: 0, opacity: 0.8 }}
        animate={{ scale: [0, 2, 3], opacity: [0.8, 0.4, 0] }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />
    </div>
  );
};

export default TileExplosion;

